import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  OAuthProvider,
  type Auth,
} from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const missing = (['apiKey', 'authDomain', 'projectId', 'appId'] as const).filter(
  (k) => !firebaseConfig[k],
);

/** Non-null when the app can't start because Firebase config is absent. */
export const configError: string | null =
  missing.length > 0
    ? `Missing Firebase config: ${missing
        .map((k) => `VITE_FIREBASE_${k.replace(/([A-Z])/g, '_$1').toUpperCase()}`)
        .join(', ')}. Copy .env.example to .env.local and fill it in.`
    : null;

let _app: FirebaseApp | undefined;
let _auth: Auth | undefined;
let _db: Firestore | undefined;
let _functions: Functions | undefined;

if (!configError) {
  _app = initializeApp(firebaseConfig);
  _auth = getAuth(_app);
  // ignoreUndefinedProperties: forms hand us `undefined` for empty optional
  // fields (phone, notes, …); treat that as "not set" rather than throwing.
  _db = initializeFirestore(_app, { ignoreUndefinedProperties: true });
  _functions = getFunctions(_app);

  if (import.meta.env.VITE_USE_EMULATORS) {
    const authPort = Number(import.meta.env.VITE_EMULATOR_AUTH_PORT ?? 9099);
    const fsPort = Number(import.meta.env.VITE_EMULATOR_FIRESTORE_PORT ?? 8080);
    const fnPort = Number(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT ?? 5001);
    connectAuthEmulator(_auth, `http://localhost:${authPort}`, { disableWarnings: true });
    connectFirestoreEmulator(_db, 'localhost', fsPort);
    connectFunctionsEmulator(_functions, 'localhost', fnPort);
  }
}

// These are only dereferenced after App checks `configError`.
export const app = _app as FirebaseApp;
export const auth = _auth as Auth;
export const db = _db as Firestore;
export const functions = _functions as Functions;

export const googleProvider = new GoogleAuthProvider();

/** Microsoft (Azure AD) provider. Inactive until the Azure app registration
 *  exists and the provider is enabled in Firebase Auth. */
export const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({ prompt: 'select_account' });

export const microsoftAuthEnabled =
  import.meta.env.VITE_ENABLE_MICROSOFT_AUTH === '1';
